import { App as CapacitorApp } from "@capacitor/app"
import { Capacitor } from "@capacitor/core"

/**
 * Android's back button — the hardware key and the edge-swipe gesture, which
 * fire the same event.
 *
 * Capacitor's default is to walk the WebView's own history and close the
 * activity when it runs out. That is wrong for this app twice over: the game
 * runs on a HashRouter, so "history" holds every modal-free route change the
 * player has made, and the last entry drops them out of the app entirely —
 * mid-game, without a word. So App.tsx registers a listener (which suppresses
 * the default outright, see NativeBackHandler) and this module is what that
 * listener is built out of.
 *
 * Two halves:
 *
 *  - a LIFO stack of handlers, for anything on screen that a back press should
 *    close before the page underneath ever hears about it. Newest wins, which
 *    is what makes a modal stacked on a modal unwind in the right order.
 *  - `minimizeApp`, for the one place where back means "leave the app": the
 *    lobby, which is this app's home screen.
 *
 * Everything here is inert outside the Capacitor build — on web and Steam the
 * browser's own back button already does the right thing, and there is no
 * listener to run the stack.
 */

export const isNativeApp = () => Capacitor.isNativePlatform()

/**
 * Android specifically, for the handful of things the platform allows and iOS
 * does not — closing the app on request being the one that matters here.
 */
export const isAndroidApp = () => Capacitor.getPlatform() === "android"

/** Returns true when it consumed the press, false to pass it further down. */
export type BackHandler = () => boolean

const handlers: BackHandler[] = []

/** Registers a handler and returns the function that takes it back off. */
export const registerBackHandler = (handler: BackHandler) => {
  handlers.push(handler)
  return () => {
    // lastIndexOf, not indexOf: two identical handlers can be registered at
    // once (the same modal component rendered twice), and the one this call
    // owns is the later of the two.
    const index = handlers.lastIndexOf(handler)
    if (index !== -1) handlers.splice(index, 1)
  }
}

/**
 * Runs the stack newest-first and stops at the first handler that takes the
 * press. Returns whether anything did.
 */
export const runBackHandlers = () => {
  // Copied, because closing something unmounts it and a handler is free to
  // unregister itself (or others) from inside this loop.
  const snapshot = [...handlers]
  for (let i = snapshot.length - 1; i >= 0; i--) {
    if (snapshot[i]()) return true
  }
  return false
}

/**
 * Sends the app to the background, the way back does from an Android app's home
 * screen — the task stays in the recents list and comes back where it was.
 *
 * Deliberately not `exitApp`: tearing the WebView down would drop the socket
 * and lose whatever game is in progress, and nobody expects a back press to do
 * that. Android-only, so the promise is allowed to reject quietly elsewhere.
 */
export const minimizeApp = () => {
  if (!isNativeApp()) return
  CapacitorApp.minimizeApp().catch(() => {})
}

/**
 * Closes the app outright, the way the Steam build's Quit button does — the
 * task leaves the recents list rather than sitting in the background.
 *
 * The opposite call to `minimizeApp` above, and deliberately so: a back press
 * is ambiguous and must not cost anyone a game, but Quit is a button the player
 * went looking for and pressed on purpose.
 *
 * Android-only. iOS gives an app no way to close itself — the plugin answers
 * `unimplemented` there — so callers gate the button on `isAndroidApp` rather
 * than showing one that silently does nothing.
 *
 * Note this leans on patches/@capacitor+app+7.1.2.patch. Stock `exitApp` is
 * just `activity.finish()`, which leaves the task sitting in the recents list
 * with the process still warm — indistinguishable from a minimise, which is
 * exactly how it behaved before the patch. The patch swaps that for
 * `finishAndRemoveTask()` + `System.exit(0)`. If a Capacitor upgrade ever drops
 * the patch, Quit silently goes back to minimising.
 */
export const exitApp = () => {
  if (!isAndroidApp()) return
  CapacitorApp.exitApp().catch(() => {})
}

/**
 * Subscribes to the native back button. Returns an unsubscribe function, and is
 * a no-op off Capacitor.
 *
 * The listener resolves asynchronously, so the cancelled flag covers the case
 * where the effect is torn down before the plugin ever hands the handle back.
 */
export const addBackButtonListener = (onBack: () => void) => {
  if (!isNativeApp()) return () => {}

  let cancelled = false
  let remove: (() => void) | null = null

  CapacitorApp.addListener("backButton", onBack)
    .then((handle) => {
      if (cancelled) handle.remove()
      else remove = () => handle.remove()
    })
    .catch(() => {})

  return () => {
    cancelled = true
    remove?.()
  }
}
